import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, Users, ChevronLeft, ChevronRight, BookUser, Filter } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { adminApi } from "@/lib/authApi";
import { publicApi } from "@/lib/publicApi";

export default function AdminDirectory() {
  const { user, setUser, logout } = useAdminSession();
  if (user === null) return <AdminLogin onSuccess={setUser} />;
  if (!user || user.pending) return <AdminShell><div className="p-10 mono-label">Loading…</div></AdminShell>;
  return (
    <AdminShell user={user} onLogout={logout}>
      <DirectoryConsole />
    </AdminShell>
  );
}

function DirectoryConsole() {
  const [regionsCatalog, setRegionsCatalog] = useState([]);
  const [stats, setStats] = useState(null);
  const [region, setRegion] = useState("");
  const [chapter, setChapter] = useState("");
  const [category, setCategory] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    publicApi.regions().then(setRegionsCatalog).catch(() => setRegionsCatalog([]));
    adminApi.get("/admin/directory/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  useEffect(() => { const t = setTimeout(() => { setQ(qInput); setPage(1); }, 300); return () => clearTimeout(t); }, [qInput]);
  useEffect(() => { setChapter(""); setPage(1); }, [region]);
  useEffect(() => { setPage(1); }, [chapter, category]);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await adminApi.get("/admin/directory/members", { params: { region: region || undefined, chapter: chapter || undefined, category: category || undefined, q: q || undefined, page, page_size: 50 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); } finally { setBusy(false); }
  }, [region, chapter, category, q, page]);
  useEffect(() => { load(); }, [load]);

  const chaptersForRegion = useMemo(() => {
    const row = regionsCatalog.find((r) => r.region === region);
    return row ? row.chapters : [];
  }, [regionsCatalog, region]);
  const topCategories = stats?.top_categories || [];
  const total = data?.total || 0;
  const pageSize = data?.page_size || 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 space-y-6" data-testid="admin-directory">
      <header className="flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <BookUser size={22} />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Member directory</h1>
            <p className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
              Master roster of BNI members across every region and chapter · {stats ? `${stats.total.toLocaleString("en-IN")} members` : "loading…"}
            </p>
          </div>
        </div>
        <button data-testid="dir-refresh" onClick={load} disabled={busy}
          className="btn-ghost inline-flex items-center gap-2">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      {stats && (
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dir-region-stats">
          {stats.regions.map((r) => (
            <button
              key={r.region} type="button"
              onClick={() => { setRegion(r.region === region ? "" : r.region); }}
              className={`hairline p-3 text-left transition hover:bg-white/5 ${region === r.region ? "ring-1 ring-[var(--red-2)]" : ""}`}
              data-testid={`dir-region-tile-${r.region}`}
            >
              <div className="mono-label text-[10px]" style={{ color: "var(--taupe)" }}>{r.region}</div>
              <div className="text-xl font-semibold mt-1">{r.count.toLocaleString("en-IN")}</div>
              <div className="text-[11px]" style={{ color: "var(--taupe)" }}>{r.chapter_count} chapters</div>
            </button>
          ))}
        </section>
      )}

      <section className="hairline p-4 space-y-3">
        <div className="flex items-center gap-2 mono-label text-[11px]" style={{ color: "var(--taupe)" }}>
          <Filter size={13} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select data-testid="dir-filter-region" className="input-x" value={region}
            onChange={(e) => setRegion(e.target.value)}>
            <option value="">All regions</option>
            {regionsCatalog.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
          </select>
          <select data-testid="dir-filter-chapter" className="input-x" value={chapter}
            disabled={!region} onChange={(e) => setChapter(e.target.value)}>
            <option value="">{region ? "All chapters" : "Pick a region first"}</option>
            {chaptersForRegion.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="dir-filter-category" className="input-x" value={category}
            onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {topCategories.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <input data-testid="dir-filter-search" type="text" className="input-x !pl-9"
              placeholder="Search name, company, phone"
              value={qInput} onChange={(e) => setQInput(e.target.value)} />
          </div>
        </div>
        <div className="flex items-center justify-between mono-label text-[11px]" style={{ color: "var(--taupe)" }}>
          <span data-testid="dir-result-count">
            {busy ? "Loading…" : `${total.toLocaleString("en-IN")} member${total === 1 ? "" : "s"}${region ? ` · ${region}` : ""}${chapter ? ` · ${chapter}` : ""}${category ? ` · ${category}` : ""}${q ? ` · "${q}"` : ""}`}
          </span>
          {(region || chapter || category || q) && (
            <button onClick={() => { setRegion(""); setChapter(""); setCategory(""); setQInput(""); }} className="underline"
              data-testid="dir-clear-filters">Clear filters</button>
          )}
        </div>
      </section>

      <section className="hairline overflow-x-auto" data-testid="dir-table">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--hairline)", color: "var(--taupe)" }}>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Name</th>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Region</th>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Chapter</th>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Company</th>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Category</th>
              <th className="text-left px-3 py-2 mono-label text-[11px]">Phone</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((m, i) => (
              <tr key={m.id || i} className="hover:bg-white/5" style={{ borderBottom: "1px solid var(--hairline)" }}
                data-testid={`dir-row-${i}`}>
                <td className="px-3 py-2 whitespace-nowrap">{m.name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{m.region}</td>
                <td className="px-3 py-2 whitespace-nowrap">{m.chapter}</td>
                <td className="px-3 py-2">{m.company || "—"}</td>
                <td className="px-3 py-2 text-[12px]" style={{ color: "var(--taupe)" }}>{m.category || "—"}</td>
                <td className="px-3 py-2 mono-label text-[11px] whitespace-nowrap">{m.phone || "—"}</td>
              </tr>
            ))}
            {!busy && (data?.items || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center" style={{ color: "var(--taupe)" }}>No members match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="flex items-center justify-end gap-3 mono-label text-[11px]" style={{ color: "var(--taupe)" }}>
        <span>Page {page} of {totalPages}</span>
        <button data-testid="dir-prev" disabled={page <= 1 || busy} onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="btn-ghost !py-1 !px-2 disabled:opacity-30"><ChevronLeft size={14} /></button>
        <button data-testid="dir-next" disabled={page >= totalPages || busy} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          className="btn-ghost !py-1 !px-2 disabled:opacity-30"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}
